// Header.jsx
import React, { useContext, useEffect, useState } from "react";
import { NavLink, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import "./navbar.css";
import generalRequest from "../../request/generalRequest";

import { Home, Book, Calendar, User, Store, Briefcase, Search, MapPin, StoreIcon, CreditCard, UserCircle, Contact, IdCard, MessagesSquare } from "lucide-react";

import ReactCountryFlag from "react-country-flag";
import translations from "../../translations/translations";
import logo from "../../images/logoTransparent.png";
import OnboardingProgressBarAdmin from "./onboardingProgressBar/onboardingProgressBarAdmin";
import OnboardingProgressBarStaff from "./onboardingProgressBar/onboardingProgressBarStaff";
import CATEGORY_OPTIONS_SEARCH from "../../constants/categoryOptionsSearch";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import BannerMessage from "../common/bannerMessage/bannerMessage";

// Pages that should show search + categories
const DISCOVERY_PREFIXES = ["/", ...CATEGORY_OPTIONS_SEARCH.map((c) => `/${c.value}`)];

const Header = () => {
	const { userData, language, setLanguage, category, setCategory, setShopsData } = useContext(CombinedContext);

	const [search, setSearch] = useState("");
	const [locationInput, setLocationInput] = useState("");

	const navigate = useNavigate();
	const role = userData?.user?.role;
	const isLoggedIn = !!userData?.user;

	const location = useLocation();

	const { pathname } = useLocation();

	const mainCategory = CATEGORY_OPTIONS_SEARCH.find((c) => pathname.startsWith(`/${c.value}`))?.value;
	const subCategory = (() => {
		const match = CATEGORY_OPTIONS_SEARCH.find((c) => pathname.startsWith(`/${c.value}`));
		if (!match) return undefined;
		const sub = pathname.replace(`/${match.value}/`, "").split("/")[0];
		return sub && sub !== match.value ? sub : undefined;
	})();

	console.log(mainCategory);
	console.log(subCategory);

	const activeMainCategory = CATEGORY_OPTIONS_SEARCH.find((c) => c.value === mainCategory);

	console.log(activeMainCategory);

	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);

	const showBanner = (type, text, duration = 7000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("showLiveBanner")) {
			showBanner("success", translations[language].checkoutPage.shopLive || "Your shop is now live 🚀");
		}
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("showLiveBannerStaff")) {
			showBanner("success", translations[language].onboarding.staff.messages.success || "Your shop is now live 🚀");
		}
	}, []);

	// Check if current page starts with any discovery prefix
	const isDiscoveryPage = DISCOVERY_PREFIXES.some((p) => pathname.startsWith(p));

	// Search on Enter only
	const handleSearchEnter = (e) => {
		if (e.key === "Enter") triggerSearch();
	};

	const triggerSearch = () => {
		const params = new URLSearchParams();
		if (search.trim()) params.set("q", search.trim());
		if (locationInput.trim()) params.set("loc", locationInput.trim());
		navigate(`/barbershop/barbershop?${params.toString()}`);
	};

	// const triggerSearch = async () => {
	//     try {
	//         const data = await generalRequest.searchShops(
	//             search.trim() || "",
	//             1,
	//             20,
	//             locationInput.trim() || undefined
	//         );
	//         setShopsData(data.shops);
	//     } catch (err) {
	//         console.error(err);
	//     }
	// };

	const handleMainCategoryClick = (cat) => {
		if (cat.subCategories?.length > 0) {
			const firstSub = cat.subCategories[0];
			navigate(`/${cat.value}/${firstSub.value}`);
			setCategory(firstSub.value);
			return;
		}
		navigate(`/${cat.value}`);
		setCategory(cat.value);
	};

	const handleSubCategoryClick = (sub) => {
		navigate(`/${activeMainCategory.value}/${sub.value}`);
		setCategory(sub.value);
	};

	// ==========================
	// RENDER COMPONENT
	// ==========================
	return (
		<div>
			<header className="sf-nav">
				<div className="sf-nav-without-progress-bar">
					<div className={`sf-nav-inner ${role === "admin" || role === "owner" || role === "staff" ? "sf-nav-inner--compact" : ""}`}>
						{/* LOGO */}
						<Link to="/" className="sf-nav-brand">
							<img src={logo} alt="ReservaSinFila" className="sf-header-logo" />
						</Link>

						{/* ==========================
                    ADMIN / OWNER LINKS
                ========================== */}
						{(role === "admin" || role === "owner") && (
							<div className="sf-nav-admin-links">
								<NavLink to="/calendar" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.calendar}
								</NavLink>

								<NavLink to="/profile" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.auth.shop}
								</NavLink>

								<NavLink to="/team" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.team}
								</NavLink>
								<NavLink to="/account/billing" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.billing}
								</NavLink>
							</div>
						)}

						{/* ==========================
                    STAFF LINKS
                ========================== */}
						{role === "staff" && (
							<div className="sf-nav-admin-links">
								<NavLink to="/calendar" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.calendar}
								</NavLink>

								<NavLink to="/staff/profile" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.profile}
								</NavLink>
							</div>
						)}

						{/* ==========================
                    CUSTOMER SEARCH BAR
                    (ONLY on discovery pages)
                ========================== */}
						{isDiscoveryPage && (!role || role === "customer") && (
							<div className="sf-nav-searchbar">
                                <div className="sf-nav-searchbar-search">
								<div className="sf-nav-chip">
									<Search size={16} className="sf-icon sf-search-click" onClick={triggerSearch} />

									<input
										className="sf-nav-input"
										placeholder={translations[language].navbar.search.placeholder}
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										onKeyDown={handleSearchEnter}
									/>
								</div>

								<div className="sf-nav-chip">
									<MapPin size={16} className="sf-icon sf-search-click" onClick={triggerSearch} />

									<input
										className="sf-nav-input"
										placeholder={translations[language].navbar.search.where}
										value={locationInput}
										onChange={(e) => setLocationInput(e.target.value)}
										onKeyDown={handleSearchEnter}
									/>
								</div>
                                </div>
								<button className="sf-nav-search-btn" onClick={triggerSearch}>
									<Search size={16} />
									<span>{translations[language].navbar.search.search || "Search"}</span>
								</button>
								
							</div>
						)}

						{/* ==========================
                    LANGUAGE + PROFILE
                ========================== */}
						<div className="sf-nav-actions">
							{/* Language Toggle */}
							
							<div className="sf-nav-lang-display" onClick={() => setLanguage(language === "en" ? "es" : "en")}>
								<ReactCountryFlag countryCode={language === "en" ? "DO" : "CA"} svg style={{ width: 20, height: 14, marginRight: 8 }} />
								<span>{language === "en" ? "Español" : "English"}</span>
							</div>

							{/* Show ONLY when NOT logged in */}
							{!isLoggedIn && (
								<button className="sf-nav-signin" onClick={() => navigate("/businesslanding")}>
									<Store size={18} className="sf-nav-signin-icon" />
									<span>{translations[language].navbar.mobile.business}</span>
								</button>
							)}

							<Link to="/contact" className="sf-nav-signin">
								<MessagesSquare size={18} className="sf-nav-signin-icon" />
								<span>{translations[language].footer.links.contact}</span>
							</Link>

							{/* Sign In / Profile */}
							{!isLoggedIn ? (
								<Link to="/signin" className="sf-nav-signin">
									<User size={18} className="sf-nav-signin-icon" />
									<span>{translations[language].navbar.auth.signin}</span>
								</Link>
							) : (
								<button className="sf-nav-signin" onClick={() => navigate("/account")}>
									<User size={18} className="sf-nav-signin-icon" />
									<span>{translations[language].navbar.admin.account}</span>
								</button>
							)}
						</div>
					</div>

					<div className="sf-nav-categories-outer">
						{isDiscoveryPage && (!role || role === "customer") && (
							<>
								{/* MAIN CATEGORIES */}
								<div className="sf-nav-categories">
									{CATEGORY_OPTIONS_SEARCH.filter((c) => !c.isFuture).map((cat) => {
										const isActiveMain = mainCategory === cat.value;

										return (
											<button
												key={cat.value}
												className={`sf-cat ${isActiveMain ? "is-active" : ""}`}
												onClick={() => handleMainCategoryClick(cat)}
											>
												{language === "en" ? cat.labelEN : cat.labelES}
											</button>
										);
									})}
								</div>

								{/* SUB CATEGORIES */}
								{activeMainCategory && activeMainCategory.subCategories?.length > 1 && (
									<div className="sf-nav-subcategories">
										{activeMainCategory.subCategories.map((sub) => (
											<button
												key={sub.value}
												className={`sf-subcat${subCategory === sub.value ? " is-active" : ""}`}
												onClick={() => handleSubCategoryClick(sub)}
											>
												{language === "en" ? sub.labelEN : sub.labelES}
											</button>
										))}
									</div>
								)}
							</>
						)}
					</div>

					<div className="sf-nav-mobile-bottom">
						{/* CUSTOMER */}
						{(!role || role === "customer") && (
							<>
								<NavLink to="/" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<Home size={22} />
									<span>{translations[language].navbar.mobile.explore}</span>
								</NavLink>

								<NavLink to="/account/bookings" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<Book size={22} />
									<span>{translations[language].navbar.mobile.bookings}</span>
								</NavLink>

								<NavLink to="/businesslanding" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<Store size={22} />
									<span>{translations[language].navbar.mobile.business}</span>
								</NavLink>

								{isLoggedIn ? (
									<button className="sf-nav-mobile-icon" onClick={() => navigate("/account")}>
										<User size={22} />
										<span>{translations[language].navbar.admin.account}</span>
									</button>
								) : (
									<NavLink to="/signin" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
										<User size={22} />
										<span>{translations[language].navbar.auth.signin}</span>
									</NavLink>
								)}
							</>
						)}

						{/* ADMIN / OWNER */}
						{(role === "admin" || role === "owner") && (
							<>
								<NavLink to="/calendar" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<Calendar size={22} />
									<span>{translations[language].navbar.admin.calendar}</span>
								</NavLink>

								<NavLink to="/profile" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<StoreIcon size={22} />
									<span>{translations[language].navbar.auth.shop}</span>
								</NavLink>

								<NavLink to="/team" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<Briefcase size={22} />
									<span>{translations[language].navbar.admin.team}</span>
								</NavLink>

								<NavLink
									to="/account/billing"
									className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""} footer-website-only`}
								>
									<CreditCard size={22} />
									{translations[language].navbar.admin.billing}
								</NavLink>

								<NavLink to="/account" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<User size={22} />
									<span>{translations[language].navbar.admin.account}</span>
								</NavLink>
							</>
						)}

						{/* STAFF */}
						{role === "staff" && (
							<>
								<NavLink to="/calendar" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<Calendar size={22} />
									<span>{translations[language].navbar.admin.calendar}</span>
								</NavLink>

								<NavLink to="/staff/profile" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
									<IdCard size={22} />
									<span>{translations[language].navbar.admin.profile}</span>
								</NavLink>

								<button className="sf-nav-mobile-icon" onClick={() => navigate("/account")}>
									<User size={22} />
									<span>{translations[language].navbar.admin.account}</span>
								</button>
							</>
						)}
					</div>
				</div>

				{/* ONBOARDING BARS */}
				{(role === "admin" || role === "owner") && <OnboardingProgressBarAdmin />}
				{role === "staff" && <OnboardingProgressBarStaff />}
			</header>
			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default Header;
